import type { CalendarDate, Reminder, Task } from "@hq/contracts";
import { nextFireAt } from "./reminders";
import { calendarDateOf } from "./time";

export type DailyReminder = { reminder: Reminder; at: string };
export type DailyTask = {
  task: Task;
  due: boolean;
  planned: boolean;
  scheduled: boolean;
  reminders: DailyReminder[];
};

/** Plans/deadlines keep their calendar date; time blocks and reminders follow the user's zone. */
export function dailyAgendaForDate(
  date: CalendarDate,
  tasks: readonly Task[],
  reminders: readonly Reminder[],
  timeZone: string,
  sourceNoteId?: string,
): { tasks: DailyTask[]; reminders: DailyReminder[] } {
  const activeTasks = new Map(
    tasks.filter((task) => !task.deletedAt).map((task) => [task.id, task]),
  );
  const rows = new Map<string, DailyTask>();
  const rowFor = (task: Task): DailyTask => ({
    task,
    due: task.dueDate === date,
    planned: task.plannedDate === date,
    scheduled:
      task.scheduledAt !== undefined && calendarDateOf(task.scheduledAt, timeZone) === date,
    reminders: [],
  });
  for (const task of activeTasks.values()) {
    const row = rowFor(task);
    if (
      row.due ||
      row.planned ||
      row.scheduled ||
      (sourceNoteId && task.sourceNoteId === sourceNoteId)
    ) {
      rows.set(task.id, row);
    }
  }
  const standalone: DailyReminder[] = [];
  for (const reminder of reminders) {
    const at = nextFireAt(reminder);
    if (!at || calendarDateOf(at, timeZone) !== date) continue;
    const entry = { reminder, at };
    if (reminder.target.type !== "task") {
      standalone.push(entry);
      continue;
    }
    const task = activeTasks.get(reminder.target.id);
    if (!task || task.status === "completed") continue;
    let row = rows.get(task.id);
    if (!row) {
      row = rowFor(task);
      rows.set(task.id, row);
    }
    row.reminders.push(entry);
  }
  for (const row of rows.values()) row.reminders.sort((a, b) => a.at.localeCompare(b.at));
  return {
    tasks: [...rows.values()].sort(
      (a, b) => a.task.sortOrder - b.task.sortOrder || a.task.id.localeCompare(b.task.id),
    ),
    reminders: standalone.sort(
      (a, b) => a.at.localeCompare(b.at) || a.reminder.id.localeCompare(b.reminder.id),
    ),
  };
}
